import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFirebase } from "../../../../context/FirebaseContext";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminProductsTranslation } from "../translations";
import {
    getProductById,
    createProduct,
    updateProduct,
} from "../../../../firebase/product-functions";
import { uploadProductImage } from "../../../../firebase/firebase";
import { getStoreCategories } from "../../../../firebase/category-functions"
import { DEFAULT_STORE_ID } from '../../../../firebase/firebase';

import ImagesTab from "./tabs/ImagesTab";
import VariantsTab from "./tabs/VariantsTab";
import SEOTab from "./tabs/SEOTab";
import ShippingTab from "./tabs/ShippingTab";
import BasicInfoTab from "./tabs/BasicInfoTab";
import "./AdminProductEditor.css";


// Default empty product
const emptyProduct = {
    name: "",
    description: "",
    price: "",
    comparePrice: "",
    cost: "",
    sku: "",
    barcode: "",
    quantity: "",
    category: "",
    status: "draft",
    images: [],
    options: [],
    variants: [],
    tags: [],
    seo: {
        title: "",
        description: "",
        keywords: ""
    },
    shippingInfo: {
        weight: "",
        width: "",
        height: "",
        length: ""
    }
};

const AdminProductEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const { t } = useAdminProductsTranslation(language);
    const isEditMode = !!id;
    const { storeInfo } = useFirebase();

    const [product, setProduct] = useState(emptyProduct);
    const [loading, setLoading] = useState(isEditMode);
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState("basic");
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [additionalImageFile, setAdditionalImageFile] = useState(null);

    // Reference to access variant image files
    const variantsTabRef = useRef();

    // Memoize stable functions to prevent re-renders
    const getTotalVariantQuantity = useCallback(() => {
        return product.variants.reduce((total, variant) => {
            return total + (parseInt(variant.quantity) || 0);
        }, 0);
    }, [product.variants]);

    const shouldValidateQuantities = useCallback(() => {
        return product.variants.length > 0 && product.quantity && parseInt(product.quantity) > 0;
    }, [product.variants.length, product.quantity]);

    const getQuantityValidation = useCallback(() => {
        if (!shouldValidateQuantities()) {
            return { isValid: true, message: "" };
        }

        const totalQuantity = parseInt(product.quantity) || 0;
        const totalVariantQuantity = getTotalVariantQuantity();

        if (totalVariantQuantity === totalQuantity) {
            return { isValid: true, message: "" };
        } else if (totalVariantQuantity < totalQuantity) {
            return {
                isValid: false,
                message: t('quantityShortfall', {
                    variantTotal: totalVariantQuantity,
                    total: totalQuantity,
                    difference: totalQuantity - totalVariantQuantity
                })
            };
        } else {
            return {
                isValid: false,
                message: t('quantityExceeded', {
                    variantTotal: totalVariantQuantity,
                    total: totalQuantity,
                    difference: totalVariantQuantity - totalQuantity
                })
            };
        }
    }, [shouldValidateQuantities, getTotalVariantQuantity, product.quantity]);

    // Auto-distribute remaining quantity to variants
    const autoDistributeQuantity = useCallback(() => {
        if (!shouldValidateQuantities()) return;

        const totalQuantity = parseInt(product.quantity) || 0;
        const totalVariantQuantity = getTotalVariantQuantity();
        const remaining = totalQuantity - totalVariantQuantity;

        if (remaining <= 0) return;

        const variantsWithZeroQuantity = product.variants.filter(v => !parseInt(v.quantity));

        if (variantsWithZeroQuantity.length > 0) {
            const perVariant = Math.floor(remaining / variantsWithZeroQuantity.length);
            const remainder = remaining % variantsWithZeroQuantity.length;

            const updatedVariants = product.variants.map(variant => {
                if (!parseInt(variant.quantity)) {
                    const index = variantsWithZeroQuantity.findIndex(v => v.id === variant.id);
                    const extraUnit = index < remainder ? 1 : 0;
                    return {
                        ...variant,
                        quantity: perVariant + extraUnit
                    };
                }
                return variant;
            });

            setProduct(prev => ({
                ...prev,
                variants: updatedVariants
            }));
        } else {
            const perVariant = Math.floor(remaining / product.variants.length);
            const remainder = remaining % product.variants.length;

            const updatedVariants = product.variants.map((variant, index) => {
                const extraUnit = index < remainder ? 1 : 0;
                return {
                    ...variant,
                    quantity: (parseInt(variant.quantity) || 0) + perVariant + extraUnit
                };
            });

            setProduct(prev => ({
                ...prev,
                variants: updatedVariants
            }));
        }
    }, [shouldValidateQuantities, getTotalVariantQuantity, product.quantity, product.variants]);

    // Load product and categories
    useEffect(() => {
        const fetchData = async () => {
            if (!storeInfo || !DEFAULT_STORE_ID) {
                console.error("Store information not available");
                navigate("/admin/login");
                return;
            }

            try {
                const categoriesList = await getStoreCategories(DEFAULT_STORE_ID);
                setCategories(categoriesList);

                if (isEditMode && id) {
                    const productData = await getProductById(DEFAULT_STORE_ID, id);
                    const formattedProduct = {
                        ...emptyProduct,
                        ...productData,
                        options: productData.options || [],
                        variants: productData.variants || [],
                        seo: productData.seo || emptyProduct.seo,
                        shippingInfo: productData.shippingInfo || emptyProduct.shippingInfo,
                        tags: productData.tags || [],
                        images: productData.images || []
                    };

                    // Ensure variants have images array initialized
                    if (formattedProduct.variants) {
                        formattedProduct.variants = formattedProduct.variants.map(variant => ({
                            ...variant,
                            images: variant.images || []
                        }));
                    }

                    setProduct(formattedProduct);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                alert("Error loading data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, isEditMode, navigate, storeInfo]);

    // Memoized change handler to prevent recreation
    const handleChange = useCallback((nameOrEvent, value) => {
        // Handle both event objects and direct name/value pairs (for multilingual inputs)
        let name, val;

        if (typeof nameOrEvent === 'string') {
            // Direct name/value pair (for multilingual inputs)
            name = nameOrEvent;
            val = value;
        } else {
            // Event object (for regular inputs)
            const event = nameOrEvent;
            name = event.target.name;
            val = event.target.value;
        }

        setProduct(prev => {
            if (name.includes(".")) {
                const [parent, child] = name.split(".");
                return {
                    ...prev,
                    [parent]: {
                        ...prev[parent],
                        [child]: val
                    }
                };
            } else {
                return {
                    ...prev,
                    [name]: val
                };
            }
        });

        setErrors(prev => {
            if (prev[name]) {
                return {
                    ...prev,
                    [name]: null
                };
            }
            return prev;
        });
    }, []);

    // Memoized setProduct to prevent unnecessary recreations
    const memoizedSetProduct = useCallback((updater) => {
        setProduct(updater);
    }, []);

    // Memoized setSelectedFiles
    const memoizedSetSelectedFiles = useCallback((updater) => {
        setSelectedFiles(updater);
    }, []);

    // Memoized setAdditionalImageFile
    const memoizedSetAdditionalImageFile = useCallback((file) => {
        setAdditionalImageFile(file);
    }, []);

    // Function to compress image
    const compressImage = useCallback((file, quality = 0.8) => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                const maxWidth = 1200;
                const maxHeight = 1200;

                let { width, height } = img;

                if (width > maxWidth || height > maxHeight) {
                    if (width > height) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    } else {
                        width = (width * maxHeight) / height;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    // Preserve the original filename by adding it as a property
                    const compressedFile = new File([blob], file.name || 'image.jpg', {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });
                    resolve(compressedFile);
                }, 'image/jpeg', quality);
            };

            img.src = URL.createObjectURL(file);
        });
    }, []);

    // Upload option value images (NEW efficient system)
    const uploadOptionValueImages = useCallback(async (productId, optionValueImageFiles) => {
        const uploadedOptionValueImages = {};

        for (const [key, files] of Object.entries(optionValueImageFiles)) {
            if (files && files.length > 0) {
                const imageUrls = [];

                for (const file of files) {
                    try {
                        const compressedFile = await compressImage(file, 0.8);
                        const imageUrl = await uploadProductImage(DEFAULT_STORE_ID, productId, compressedFile);
                        imageUrls.push(imageUrl);
                    } catch (error) {
                        console.error(`Error uploading image for option value ${key}:`, error);
                    }
                }

                if (imageUrls.length > 0) {
                    uploadedOptionValueImages[key] = imageUrls;
                }
            }
        }

        return uploadedOptionValueImages;
    }, [compressImage]);

    // Form validation
    const validateForm = useCallback(() => {
        const newErrors = {};

        if (!product.name) newErrors.name = "Product name is required";
        if (!product.price) newErrors.price = "Price is required";
        if (isNaN(parseFloat(product.price))) newErrors.price = "Price must be a number";
        if (!product.category) newErrors.category = "Category is required";

        const quantityValidation = getQuantityValidation();
        if (!quantityValidation.isValid) {
            newErrors.quantity = quantityValidation.message;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [product.name, product.price, product.category, getQuantityValidation]);

    // Handle form submission
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            const firstError = document.querySelector(".field-error");
            if (firstError) {
                firstError.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }

        if (!storeInfo || !DEFAULT_STORE_ID) {
            alert("Store information not available. Please log in again.");
            navigate("/admin/login");
            return;
        }

        // Check product limit for new products only (not when editing)
        // Note: Product limits are enforced server-side in Cloud Functions
        // The createProduct function will return 'resource-exhausted' error if limit reached

        setSaving(true);

        try {
            let productId = id;
            let productData = {
                ...product,
                price: parseFloat(product.price),
                quantity: product.quantity ? parseInt(product.quantity) : 0
            };
            // Ensure status is always lowercase for server validation
            // if (productData.status) {
            //     productData.status = productData.status.toLowerCase();
            // }

            // Filter out base product images that are URLs (already uploaded)
            const currentImages = product.images.filter(img => typeof img === 'string' && img.startsWith('http'));
            productData.images = currentImages;

            // Create or update the product first
            if (isEditMode) {
                await updateProduct(DEFAULT_STORE_ID, productId, productData);
            } else {
                productId = await createProduct(DEFAULT_STORE_ID, productData);
            }

            // Upload base product images if any
            if (selectedFiles.length > 0) {
                for (const file of selectedFiles) {
                    try {
                        const compressedFile = await compressImage(file, 0.8);
                        console.log('🔄 Attempting upload for file:', file.name);
                        const imageUrl = await uploadProductImage(DEFAULT_STORE_ID, productId, compressedFile);
                        productData.images = [...productData.images, imageUrl];
                        console.log('✅ Upload successful for file:', file.name);
                    } catch (uploadError) {
                        console.error('❌ Upload failed for file:', file.name, uploadError);
                        throw new Error(`Failed to upload image "${file.name}": ${uploadError.message}`);
                    }
                }
            }

            // Upload additional image if any
            if (additionalImageFile) {
                try {
                    const compressedFile = await compressImage(additionalImageFile, 0.8);
                    console.log('🔄 Attempting upload for additional image:', additionalImageFile.name);
                    const imageUrl = await uploadProductImage(DEFAULT_STORE_ID, productId, compressedFile);
                    productData.imageUrl = imageUrl;
                    console.log('✅ Additional image upload successful:', additionalImageFile.name);
                } catch (uploadError) {
                    console.error('❌ Additional image upload failed:', additionalImageFile.name, uploadError);
                    throw new Error(`Failed to upload additional image "${additionalImageFile.name}": ${uploadError.message}`);
                }
            }

            // Get option value image files from VariantsTab (NEW efficient system)
            const optionValueImageFiles = variantsTabRef.current?.getOptionValueImageFiles?.() || {};

            // Upload option value images
            const uploadedOptionValueImages = await uploadOptionValueImages(productId, optionValueImageFiles);

            // Update options with uploaded images
            if (Object.keys(uploadedOptionValueImages).length > 0) {
                const updatedOptions = productData.options.map(option => {
                    const updatedValueImages = { ...(option.valueImages || {}) };

                    // Check each value for this option
                    option.values.forEach(value => {
                        const key = `${option.id}:${value}`;
                        if (uploadedOptionValueImages[key]) {
                            updatedValueImages[value] = [
                                ...(updatedValueImages[value] || []),
                                ...uploadedOptionValueImages[key]
                            ];
                        }
                    });

                    return { ...option, valueImages: updatedValueImages };
                });

                productData.options = updatedOptions;
            }

            // Final update with all images
            await updateProduct(DEFAULT_STORE_ID, productId, {
                images: productData.images,
                options: productData.options,
                variants: productData.variants,
                ...(productData.imageUrl && { imageUrl: productData.imageUrl })
            });

            navigate("/admin/products");
        } catch (error) {
            console.error("Error saving product:", error);
            alert("Failed to save product: " + (error.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    }, [validateForm, storeInfo, id, isEditMode, navigate, product, selectedFiles, additionalImageFile, compressImage, uploadOptionValueImages]);

    // Memoize the product object to prevent unnecessary re-renders
    const memoizedProduct = useMemo(() => product, [product]);

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <p>Loading product data...</p>
            </div>
        );
    }

    return (
        <div className="admin-product-editor">


            <form onSubmit={handleSubmit}>
                <div className="editor-header">
                    <h1>{isEditMode ? t('editProduct') : t('newProduct')}</h1>
                    <div className="editor-actions">
                        <button
                            type="button"
                            className="editor-secondary-btn"
                            onClick={() => navigate("/admin/products")}
                            disabled={saving}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            className="editor-primary-btn"
                            disabled={saving}
                        >
                            {saving ? t('saving') : t('saveProduct')}
                        </button>
                    </div>
                </div>

                <div className="editor-content">
                    <div className="editor-tabs">
                        <button
                            type="button"
                            className={`editor-tab ${activeTab === "basic" ? "active" : ""}`}
                            onClick={() => setActiveTab("basic")}
                        >
                            {t('basicInfo')}
                        </button>
                        <button
                            type="button"
                            className={`editor-tab ${activeTab === "images" ? "active" : ""}`}
                            onClick={() => setActiveTab("images")}
                        >
                            {t('images')}
                        </button>
                        <button
                            type="button"
                            className={`editor-tab ${activeTab === "variants" ? "active" : ""} ${!getQuantityValidation().isValid ? "tab-error" : ""}`}
                            onClick={() => setActiveTab("variants")}
                        >
                            {t('variants')}
                            {!getQuantityValidation().isValid && <span className="tab-error-indicator">!</span>}
                        </button>
                        {/* <button
                            type="button"
                            className={`editor-tab ${activeTab === "seo" ? "active" : ""}`}
                            onClick={() => setActiveTab("seo")}
                        >
                            {t('seo')}
                        </button> */}
                        <button
                            type="button"
                            className={`editor-tab ${activeTab === "shipping" ? "active" : ""}`}
                            onClick={() => setActiveTab("shipping")}
                        >
                            {t('shipping')}
                        </button>
                    </div>

                    <div className="editor-form-container">
                        <BasicInfoTab
                            product={memoizedProduct}
                            setProduct={memoizedSetProduct}
                            handleChange={handleChange}
                            errors={errors}
                            categories={categories}
                            getTotalVariantQuantity={getTotalVariantQuantity}
                            shouldValidateQuantities={shouldValidateQuantities}
                            getQuantityValidation={getQuantityValidation}
                            autoDistributeQuantity={autoDistributeQuantity}
                            activeTab={activeTab}
                            additionalImageFile={additionalImageFile}
                            setAdditionalImageFile={memoizedSetAdditionalImageFile}
                        />

                        <ImagesTab
                            product={memoizedProduct}
                            setProduct={memoizedSetProduct}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={memoizedSetSelectedFiles}
                            activeTab={activeTab}
                        />

                        <VariantsTab
                            ref={variantsTabRef}
                            product={memoizedProduct}
                            setProduct={memoizedSetProduct}
                            getTotalVariantQuantity={getTotalVariantQuantity}
                            shouldValidateQuantities={shouldValidateQuantities}
                            getQuantityValidation={getQuantityValidation}
                            autoDistributeQuantity={autoDistributeQuantity}
                            activeTab={activeTab}
                        />

                        {/* <SEOTab
                            product={memoizedProduct}
                            handleChange={handleChange}
                            activeTab={activeTab}
                        /> */}

                        <ShippingTab
                            product={memoizedProduct}
                            handleChange={handleChange}
                            activeTab={activeTab}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AdminProductEditor;